<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { Building2, Plus, RefreshCw, Save, ShieldCheck, SlidersHorizontal, Unlink } from 'lucide-vue-next'
import { ElMessage } from 'element-plus'
import { api, errorText, object, rows, type JsonRecord } from '../api'
import { useAppStore } from '../store'

const store = useAppStore()
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const tab = ref<'enterprise' | 'admin' | 'defaults'>('enterprise')
const form = reactive({ name: '', persona: '', greeting: '您好，欢迎咨询。请告诉我您想了解什么？', knowledge_top_k: 16, script_top_k: 16, memory_top_k: 16, recent_customer_messages: 256, recent_contact_messages: 256 })
/** 客服自有渠道 + 历史企业渠道（非通知用途），按原有答疑链路展示。 */
const answerChannels = ref<string[]>([])
/** 管理员专属通知机器人（channels 里 purpose=notify 的条目）。 */
const notifyChannels = ref<{ key: string; appId: string; credentialsConfigured: boolean }[]>([])

const adminNotify = ref<JsonRecord>({})
const adminForm = reactive({ channel: '', receiver_id_type: 'open_id', receiver_id: '' })
const bindSaving = ref(false)
/** 通知渠道下拉：管理员专属机器人优先，其次客服自有渠道与历史企业渠道。 */
const feishuChannels = computed(() => Array.from(new Set([...notifyChannels.value.map(item => item.key), ...answerChannels.value])))
const adminBound = computed(() => adminNotify.value.bound === true)
const adminChannel = computed(() => String(adminNotify.value.channel ?? ''))

const notifyDialog = ref(false)
const notifySaving = ref(false)
const notifyForm = reactive({ key: '', app_id: '', app_secret: '', verification_token: '' })
/** 正在更新的渠道（已存在则密钥留空=保持不变）。 */
const notifyEditing = ref(false)
function openNotifyDialog(key = '') {
  const existing = notifyChannels.value.find(item => item.key === key)
  notifyEditing.value = !!existing
  Object.assign(notifyForm, { key: existing?.key ?? '', app_id: existing?.appId ?? '', app_secret: '', verification_token: '' })
  notifyDialog.value = true
}
async function saveNotifyChannel() {
  const key = notifyForm.key.trim()
  if (!/^[A-Za-z0-9_-]{1,40}$/.test(key)) { ElMessage.warning('渠道标识只允许字母、数字、下划线、中划线（≤40）'); return }
  if (!notifyEditing.value && answerChannels.value.includes(key)) { ElMessage.warning(`渠道标识「${key}」已被客服使用，换一个或先改客服配置`); return }
  if (!notifyForm.app_id.trim()) { ElMessage.warning('请填写 App ID'); return }
  if (!notifyForm.app_secret.trim() && !notifyEditing.value) { ElMessage.warning('请填写 App Secret'); return }
  if (!notifyForm.verification_token.trim() && !notifyEditing.value) { ElMessage.warning('请填写 Verification Token（为后续升级收发一体预留）'); return }
  notifySaving.value = true
  try {
    // 只提交这一条渠道：服务端按 key upsert，空白密钥保留旧密文，明文凭据加密存储。
    const channel: JsonRecord = { type: 'feishu_app', enabled: true, purpose: 'notify', config: { app_id: notifyForm.app_id.trim(), app_secret: notifyForm.app_secret.trim(), verification_token: notifyForm.verification_token.trim() } }
    await api.put('/enterprise', { config: { channels: { [key]: channel } } })
    ElMessage.success(notifyEditing.value ? `通知渠道「${key}」已更新` : `通知渠道「${key}」已添加`)
    notifyDialog.value = false
    await load()
    adminForm.channel = key
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { notifySaving.value = false }
}

async function load() {
  loading.value = true; error.value = ''
  try {
    const [enterpriseResponse, agentsResponse] = await Promise.all([api.get('/enterprise'), api.get('/agents')])
    const response = object(enterpriseResponse)
    store.enterprise = response
    const config = object(response.config)
    const defaults = object(config.defaults)
    const retrieval = object(defaults.retrieval)
    const context = object(defaults.context)
    Object.assign(form, { name: String(config.name ?? response.name ?? ''), persona: String(defaults.persona ?? ''), greeting: String(defaults.greeting ?? '您好，欢迎咨询。请告诉我您想了解什么？'), knowledge_top_k: Number(retrieval.knowledge_top_k ?? 16), script_top_k: Number(retrieval.script_top_k ?? 16), memory_top_k: Number(retrieval.memory_top_k ?? 16), recent_customer_messages: Number(context.recent_customer_messages ?? 256), recent_contact_messages: Number(context.recent_contact_messages ?? 256) })
    const agentChannels = rows(agentsResponse).flatMap(agent => {
      const bot = object(object(agent.config).bot)
      const key = String(bot.channel ?? '').trim()
      return key && String(bot.app_id ?? '').trim() ? [key] : []
    })
    const entries = Object.entries(object(config.channels))
    const isFeishu = (value: unknown) => { const row = object(value); return row.enabled === true && String(row.type ?? '').startsWith('feishu') }
    // 管理员专属通知机器人：purpose=notify，只服务于本卡片。
    notifyChannels.value = entries
      .filter(([, value]) => isFeishu(value) && String(object(value).purpose ?? '') === 'notify')
      .map(([key, value]) => {
        const row = object(value)
        return { key, appId: String(object(row.config).app_id ?? ''), credentialsConfigured: row.credentialsConfigured === true }
      })
    // 其余飞书渠道继续可被通知复用：客服自有渠道 + 历史企业渠道（兼容旧绑定）。
    const legacy = entries
      .filter(([, value]) => isFeishu(value) && String(object(value).purpose ?? '') !== 'notify')
      .map(([key]) => key)
    answerChannels.value = [...agentChannels, ...legacy]
    adminNotify.value = object(config.admin_notify)
    if (!adminForm.channel) adminForm.channel = String(adminNotify.value.channel ?? '')
  } catch (cause) { error.value = errorText(cause) } finally { loading.value = false }
}
async function save() {
  if (!form.name.trim()) { ElMessage.warning('企业名称不能为空'); return }
  saving.value = true
  try {
    // 渠道应用已迁到 AI 客服配置；这里只保存企业名称与默认口径，不再回写 channels
    const config: JsonRecord = { schema_version: 1, name: form.name.trim(), defaults: { persona: form.persona, greeting: form.greeting, retrieval: { knowledge_top_k: form.knowledge_top_k, script_top_k: form.script_top_k, memory_top_k: form.memory_top_k }, context: { recent_customer_messages: form.recent_customer_messages, recent_contact_messages: form.recent_contact_messages } } }
    await api.put('/enterprise', { config })
    await store.refreshEnterprise()
    ElMessage.success('企业配置已保存')
    await load()
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { saving.value = false }
}

async function adminOneClick() {
  if (!adminForm.channel) { ElMessage.warning('请先选择用于通知的飞书渠道'); return }
  bindSaving.value = true
  try {
    const result = object(await api.post('/enterprise/admin-notify-bind', { channel: adminForm.channel }))
    const url = String(result.authorizeUrl ?? '')
    if (!url) { ElMessage.error('未获得授权链接'); return }
    window.open(url, '_blank', 'noopener')
    ElMessage.success('已在飞书授权页打开：本人扫码/登录授权后会自动完成绑定，之后可点「发送测试」确认')
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { bindSaving.value = false }
}
async function adminManualSave() {
  bindSaving.value = true
  try {
    await api.put('/enterprise/admin-notify', { channel: adminForm.channel, receiver_id_type: adminForm.receiver_id_type, receiver_id: adminForm.receiver_id })
    ElMessage.success('管理员通知已保存（地址在服务端加密）')
    adminForm.receiver_id = ''
    await load()
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { bindSaving.value = false }
}
async function adminTest() {
  bindSaving.value = true
  try {
    const result = object(await api.post('/enterprise/admin-notify-test'))
    if (result.sent === true) ElMessage.success('测试消息已发送，请在飞书查看')
    else ElMessage.warning(`发送失败：${String(result.reason ?? '未知原因')}`)
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { bindSaving.value = false }
}
async function adminClear() {
  bindSaving.value = true
  try { await api.put('/enterprise/admin-notify', { clear: true }); ElMessage.success('已解除管理员通知绑定'); adminNotify.value = {}; await load() }
  catch (cause) { ElMessage.error(errorText(cause)) } finally { bindSaving.value = false }
}
onMounted(load)
</script>

<template>
  <div class="page-header"><div><h1 class="page-title">企业设置</h1><p class="page-description">企业默认口径、管理员通知与上下文参数。渠道应用已迁移到每个 AI 客服的配置页。</p></div><div class="page-actions"><el-button :icon="RefreshCw" :loading="loading" @click="load">重新读取</el-button><el-button type="primary" :icon="Save" :loading="saving" @click="save">保存设置</el-button></div></div>
  <div class="page-content"><div v-if="error" class="error-banner">{{ error }}</div><div class="settings-layout"><nav class="settings-nav" role="tablist" aria-label="企业设置分类"><button role="tab" :aria-selected="tab==='enterprise'" :class="{active:tab==='enterprise'}" @click="tab='enterprise'"><Building2 :size="15"/>企业信息</button><button role="tab" :aria-selected="tab==='admin'" :class="{active:tab==='admin'}" @click="tab='admin'"><ShieldCheck :size="15"/>管理员通知</button><button role="tab" :aria-selected="tab==='defaults'" :class="{active:tab==='defaults'}" @click="tab='defaults'"><SlidersHorizontal :size="15"/>默认参数</button></nav>
    <section class="settings-panel" v-loading="loading">
      <template v-if="tab==='enterprise'"><h2>企业信息</h2><p>企业显示名称及统一默认服务口径。</p><el-form label-position="top" style="max-width:600px"><el-form-item label="企业名称"><el-input v-model="form.name" maxlength="160"/></el-form-item></el-form></template>
      <template v-else-if="tab==='admin'">
        <div class="channel-toolbar"><div><h2>企业管理员通知</h2><p class="admin-intro">用于企业总管理接收特殊情况提醒（额度不足、答疑路由异常等）。绑定本人的飞书后，系统会通过所选飞书渠道私聊通知。</p></div><el-button type="primary" plain :icon="Plus" @click="openNotifyDialog()">添加通知渠道</el-button></div>
        <div v-if="notifyChannels.length" class="notify-list">
          <div v-for="item in notifyChannels" :key="item.key" class="notify-row">
            <span><strong>{{ item.key }}</strong><small>App ID {{ item.appId || '—' }}</small><span class="status-tag" :class="item.credentialsConfigured ? 'active' : 'pending'">{{ item.credentialsConfigured ? '凭据已配置' : '凭据未配置' }}</span></span>
            <el-button class="row-action" @click="openNotifyDialog(item.key)">更新凭据</el-button>
          </div>
          <p class="credential-note">通知渠道默认只给管理员发提醒；如果后期想让它同时承接导师答疑，把它绑定给联系人、再在 AI 客服页选择该渠道即可——一个渠道最多服务一个客服，重复选择会被占用校验拦下。用同一标识再次提交即覆盖凭据（服务端加密保存、不回显）。</p>
        </div>
        <div class="channel-card">
          <div class="channel-head"><span><strong>当前状态</strong><span class="status-tag" :class="adminBound ? 'active' : 'pending'">{{ adminBound ? `已绑定 · ${adminChannel}` : '未绑定' }}</span></span></div>
          <p v-if="adminBound" class="credential-note">绑定时间 {{ String(adminNotify.bound_at ?? '').slice(0, 19).replace('T', ' ') }} · 绑定方式 {{ adminNotify.bind_source === 'feishu_oauth' ? '飞书一键授权' : '手动录入' }}</p>
          <div v-if="!feishuChannels.length" class="config-check warn"><strong>尚不可绑定</strong><div>点右上角「添加通知渠道」建一个专属通知机器人，或先在「AI 客服」页给客服配置渠道应用。</div></div>
          <template v-else>
            <div class="form-grid">
              <el-form-item label="通知渠道"><el-select v-model="adminForm.channel" style="width:100%"><el-option v-for="key in feishuChannels" :key="key" :label="notifyChannels.some(item => item.key === key) ? `${key}（专属通知机器人）` : key" :value="key"/></el-select></el-form-item>
              <el-form-item label="接收地址类型（手动录入时）"><el-select v-model="adminForm.receiver_id_type"><el-option v-for="value in ['open_id','user_id','union_id','email','chat_id']" :key="value" :label="value" :value="value"/></el-select></el-form-item>
              <el-form-item label="接收地址（手动录入时）"><el-input v-model="adminForm.receiver_id" type="password" show-password autocomplete="off" placeholder="留空保持已绑定地址"/><span class="credential-note">推荐用「一键授权」自动获取，无需手填。</span></el-form-item>
            </div>
            <div class="toolbar admin-actions">
              <el-button type="primary" :loading="bindSaving" @click="adminOneClick">一键授权绑定（飞书）</el-button>
              <el-button :loading="bindSaving" @click="adminManualSave">手动保存</el-button>
              <el-button v-if="adminBound" :loading="bindSaving" @click="adminTest">发送测试</el-button>
              <el-button v-if="adminBound" class="danger-button" :loading="bindSaving" @click="adminClear"><Unlink :size="14" />解除绑定</el-button>
            </div>
          </template>
        </div>
        <el-dialog v-model="notifyDialog" :title="notifyEditing ? '更新通知渠道凭据' : '添加通知渠道'" width="520px" destroy-on-close>
          <el-form label-position="top">
            <el-form-item label="渠道标识" required><el-input v-model="notifyForm.key" :disabled="notifyEditing" maxlength="40" placeholder="例如 notify_main"/><span v-if="!notifyEditing" class="credential-note">字母、数字、下划线或中划线；与已有标识重名将覆盖该渠道凭据</span></el-form-item>
            <el-form-item label="App ID" required><el-input v-model="notifyForm.app_id" maxlength="100" placeholder="cli_xxxxxxxx"/></el-form-item>
            <el-form-item label="App Secret" :required="!notifyEditing"><el-input v-model="notifyForm.app_secret" type="password" show-password autocomplete="new-password" :placeholder="notifyEditing ? '留空保持原值，输入则覆盖' : '服务端加密保存，不回显'"/></el-form-item>
            <el-form-item label="Verification Token" :required="!notifyEditing"><el-input v-model="notifyForm.verification_token" type="password" show-password autocomplete="new-password" :placeholder="notifyEditing ? '留空保持原值，输入则覆盖' : '事件订阅校验 token'"/><span class="credential-note">当前只发不收暂时用不到，按你的要求一并录入：后续把通知机器人升级成可收发时无需补录。</span></el-form-item>
          </el-form>
          <template #footer><div class="dialog-actions"><el-button @click="notifyDialog=false">取消</el-button><el-button type="primary" :loading="notifySaving" @click="saveNotifyChannel">{{ notifyEditing ? '保存并覆盖凭据' : '添加渠道' }}</el-button></div></template>
        </el-dialog>
      </template>
      <template v-else><h2>默认参数</h2><p>客服未设置覆盖值时继承企业级默认值。</p><el-form label-position="top"><div class="section-label">默认人设</div><el-form-item><el-input v-model="form.persona" type="textarea" :rows="4" maxlength="10000" placeholder="说明客服角色、语气与回答边界"/></el-form-item><el-form-item label="默认问候语"><el-input v-model="form.greeting" type="textarea" :rows="2" maxlength="1000" placeholder="客户打开会话时显示"/></el-form-item><div class="section-label">每轮检索条数</div><div class="form-grid"><el-form-item label="知识条数"><el-input-number v-model="form.knowledge_top_k" :min="0" :max="128"/></el-form-item><el-form-item label="话术条数"><el-input-number v-model="form.script_top_k" :min="0" :max="128"/></el-form-item><el-form-item label="记忆条数"><el-input-number v-model="form.memory_top_k" :min="0" :max="128"/></el-form-item></div><div class="section-label">提示词上下文条数</div><div class="form-grid"><el-form-item label="客户最近消息"><el-input-number v-model="form.recent_customer_messages" :min="0" :max="256"/></el-form-item><el-form-item label="联系人最近消息"><el-input-number v-model="form.recent_contact_messages" :min="0" :max="256"/></el-form-item></div><div class="config-check"><ShieldCheck :size="14"/> 联系人上下文仅供内部答疑与教学使用，不会并入客户侧对话历史。</div></el-form></template>
    </section></div>
  </div>
</template>

<style scoped>
.channel-toolbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 12px; }
.channel-toolbar p { color: var(--muted); font-size: 12px; margin-top: 4px; }
.channel-card { border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; }
.channel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; gap: 12px; }
.channel-head > span:first-child { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.channel-head small { color: var(--muted); margin-left: 8px; }
.channel-actions { display: flex; align-items: center; gap: 10px; }
.admin-intro { color: var(--muted); font-size: 13px; }
.admin-actions { margin-top: 6px; }
.notify-list { border: 1px solid var(--line); border-radius: 12px; padding: 4px 16px; margin-bottom: 14px; }
.notify-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--line-light); }
.notify-row:last-of-type { border-bottom: 0; }
.notify-row > span { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.notify-row small { color: var(--muted); }
.notify-list .credential-note { padding: 10px 0 12px; }
</style>
